import React, { useCallback } from 'react';

import { gql } from '@apollo/client';
import { Button, Stack } from '@mui/material';

import {
  ActivityLogAgentQuestion_ActivityLogFragment,
  SourceType,
  useActivityNoteCreateMutation,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { useIsQuestionAnswered } from 'src/entities/ActivityLog/AnsweredQuestionsContext';
import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import ActivityLogCommentBody from 'src/entities/ActivityLog/Helpers/ActivityLogCommentBody';
import ActivityLogContent from 'src/entities/ActivityLog/Helpers/ActivityLogContent';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

interface Fragments {
  activityLog: ActivityLogAgentQuestion_ActivityLogFragment;
}

type ActivityLogContentShape = {
  markdown?: string | null;
};

const ActivityLogAgentQuestion: FCWithFragments<Fragments> = ({ activityLog }) => {
  const aiTaskEnabled = useIsAiTaskEnabled();
  const [createNote] = useActivityNoteCreateMutation();
  const refetch = useActivityFeedRefetch(SourceType.SourceTypeTask);
  const isAnswered = useIsQuestionAnswered(activityLog.id);

  const handleOptionClick = useCallback(
    (option: string) => {
      // eslint-disable-next-line no-underscore-dangle
      const sourceId = activityLog.source?.__typename === 'Task' ? activityLog.source.id : undefined;
      if (!sourceId) {
        return;
      }

      createNote({
        variables: {
          input: {
            note: option,
            sourceId,
            sourceType: SourceType.SourceTypeTask,
          },
        },
      })
        .then(() => {
          refetch();
          return undefined;
        })
        .catch(() => {});
    },
    [activityLog.source, createNote, refetch]
  );

  if (!aiTaskEnabled) {
    return null;
  }

  const { details } = activityLog;
  // eslint-disable-next-line no-underscore-dangle
  const isQuestionDetails = details?.__typename === 'ActivityLogAgentQuestionDetails';
  const question = isQuestionDetails ? details.question : '';
  const markdown = isQuestionDetails
    ? ((details as typeof details & { content?: ActivityLogContentShape | null }).content?.markdown ?? null)
    : null;
  const options = isQuestionDetails ? details.options : [];

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actionLabelKey="hiring-dashboard-candidates:candidates.activityLog.agentQuestion"
      actionLabelValues={{}}
    >
      <Stack direction="column" spacing={1.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
        {question ? (
          <ActivityLogContent
            fullWidth
            sx={{ borderRadius: '6px 12px 12px 12px', overflow: 'hidden', ml: 0 }}
            content={<ActivityLogCommentBody legacyText={question} markdown={markdown} />}
          />
        ) : null}
        {!isAnswered && options.length > 0 ? (
          <Stack direction="column" spacing={0.75} sx={{ maxWidth: '100%' }}>
            {options.map((option) => (
              <Button
                key={option}
                variant={'outlined' as 'contained'}
                size="small"
                onClick={() => handleOptionClick(option)}
                sx={{
                  textTransform: 'none',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  fontWeight: 500,
                  fontSize: 13,
                  lineHeight: 1.4,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: '8px',
                  borderColor: 'divider',
                  color: 'text.primary',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {option}
              </Button>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </ActivityLogBase>
  );
};

ActivityLogAgentQuestion.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogAgentQuestion_activityLog on ActivityLog {
      id
      source {
        ... on Task {
          id
        }
      }
      details {
        ... on ActivityLogAgentQuestionDetails {
          __typename
          question
          options
          content {
            markdown
            plaintext
            formatVersion
            editorStateJson
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogAgentQuestion;
